import { writable, get } from "svelte/store"
import { getParameterByName, getProductPrice, LRUCache } from "./utils"
import { getCountry, getCategoryPathMap, getContent, _fetch, getProducts } from "./api"
import * as SSR from "../../api/hooks/lib/ssr.js"
import { defaultVAT } from "../../api/hooks/config-client"
import type { ComponentType } from "svelte"
import { setUser } from "./sentry"
import { apiBaseURL, baseURL } from "./config"
import { jwtDecode } from "jwt-decode"
import { navigate } from "../../vendor/svelte-routing"

const initLoc = {
    path: (typeof window !== "undefined" && window.location?.pathname) || "/",
    search: (typeof window !== "undefined" && window.location?.search) || "",
    hash: (typeof window !== "undefined" && window.location?.hash) || "",
    push: false,
    pop: false,
    categoryPath: "",
    url: `${baseURL}${(typeof window !== "undefined" && window.location?.pathname) || "/"}`,
}
initLoc.categoryPath = initLoc.path.replace(/\/\d{4,99}[^\/]+$/, "")

export const location = writable(initLoc)

const pathMap: { [key: string]: CategoryNode } = {}
export const categoryPathMap = writable(pathMap)
;(async () => {
    try {
        const pathMap = await getCategoryPathMap()
        categoryPathMap.set(pathMap)
    } catch (e) {
        console.log("SETPATHMAP ERROR", JSON.stringify(e), e)
    }
})()

class LRULocationCache<T> extends LRUCache<T> {
    public read(): T {
        return super.get(get(location).path)
    }
    public write(value: T) {
        super.put(get(location).path, value)
    }
}

////////////////////// BREADCRUMB

let breadNodes: CategoryNode[] = []
export const breadcrumbs = writable(breadNodes)

export const setBreadcrumbs = (l: typeof initLoc) => {
    let path = l.categoryPath
    const categoryMap = get(categoryPathMap)
    breadNodes = []
    if (path) {
        let node = categoryMap[path]
        while (node) {
            if (!node.is_root) breadNodes.unshift(node)
            node = categoryMap[node.parent]
        }
    }
    breadcrumbs.set(breadNodes)
}

location.subscribe((l) => {
    setBreadcrumbs(l)
})
categoryPathMap.subscribe(() => {
    setBreadcrumbs(get(location))
})

///////////////////////// CACHE

export let productListCache = writable(
    new LRUCache<{
        data: Product[]
        count: number
    }>()
)

export let productFilterCache = writable(
    new LRULocationCache<{
        data: Product[]
        count: number
        sort?: string
        selection?: any
        filter?: any
    }>()
)

let _content: Content
export const content = writable(_content)

export let contentCache = writable(new LRULocationCache<Content>())

const api2storeContent = async (path: string) => {
    content.set(null)
    let c: Content
    try {
        c = await getContent(path)
    } catch (e) {
        console.log(e)
    }
    content.set(c)
    get(contentCache).write(c)
}
let _oldPath: string
location.subscribe((l) => {
    if (_oldPath != l.path) {
        _oldPath = l.path
        const c = get(contentCache).read()
        if (c) {
            content.set(c)
        } else {
            api2storeContent(l.path)
        }
    }
})

////////////////////// Overlays

export let cartOverlay = writable(false)
export let dealerLoginOverlay = writable(false)
export let mobileSearchOverlay = writable(false)
export let contentOverlay = writable<
    string | { component: ComponentType; props?: { [key: string]: any }; hideClose?: boolean }
>("")

if (typeof window !== "undefined") {
    // @ts-ignore
    window["openOverlay"] = (href: string) => {
        const h = window.location.protocol + "//" + window.location.host
        if (href.startsWith(h)) {
            href = href.slice(h.length)
        }
        contentOverlay.set(href)
        return false
    }
}

////////////////////// Händlerlogin

export const login = writable<Login | null>()
login.subscribe(($login) => {
    // track user to sentry
    setUser({
        username: $login?.account?.ident && $login.account.ident.dealerNumber + "-" + $login.account.ident.personNumber,
        email: $login?.account?.email,
    })
})

export class LoginError extends Error {
    constructor(
        public message: string,
        public type: "fail" | "mfa" | "no_order_permission",
        public data?: any
    ) {
        super(message)
        this.data = data
    }
}

export const postLogin = async (
    data: { username?: string; password?: string; mfaCode?: string; mfaToken?: string },
    signal?: AbortSignal
): Promise<Login> => {
    const r = await _fetch(apiBaseURL + "login", {
        method: "POST",
        mode: "cors",
        headers: {
            "Content-Type": "application/json",
            ...(data.mfaCode ? { Authorization: "Bearer " + data.mfaToken } : {}),
        },
        body: JSON.stringify({
            username: data.username,
            password: data.password,
            mfaCode: data.mfaCode,
        }),
        signal,
    })

    const d = await r.json()

    if (d?.token) {
        const _l: Login = {
            tokenString: d.token,
            tokenData: jwtDecode<JWTLoginClaims>(d.token),
            account: d.account,
        }
        login.set(_l)
        return _l
    }

    // treat as mfa data
    const mfaData: {
        mfaRequired: boolean
        mfaToken: string
    } = d
    if (mfaData.mfaRequired) {
        throw new LoginError("MFA required", "mfa", mfaData)
    }

    // delete eshopToken cookie on failed login, it is set in backend on successful login
    if (typeof document !== "undefined") {
        document.cookie = "eshopToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;"
    }

    login.set(null)

    throw new LoginError("Login failed", d.code || "fail", d)
}

export const postLogout = async (): Promise<true> => {
    await fetch(apiBaseURL + "login?logout=1", {
        method: "POST",
        mode: "cors",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
    })

    navigate("/", { replace: true })
    login.set(null)

    return true
}

let refreshingToken: Promise<Login> | null
export const getLogin = async (signal?: AbortSignal): Promise<Login | null> => {
    let _l = get(login)
    if ((_l?.tokenData?.exp || 0) - 60 < new Date().getTime() / 1000) {
        console.log("login token expired")
        // token expired, relogin via refresh cookie, run only one in parallel
        if (!refreshingToken) {
            refreshingToken = postLogin({}, signal)
        }
        try {
            _l = await refreshingToken
        } finally {
            refreshingToken = null
        }
    }

    // console.log("login", _l)

    return _l
}

// init login state
getLogin()
    .catch((e) => {
        console.log("getting login via refresh cookie failed")
    })
    .finally(() => {
        console.log("login state initialized")
    })

if (typeof window !== "undefined") {
    // refresh login on token expiration

    let refreshTimeout: any = null

    const getDiff = (l: Login) => {
        const exp = ((l.tokenData.exp || 0) - 60) * 1000
        const now = new Date().getTime()
        const diff = exp - now
        return diff
    }

    const refreshLogin = async () => {
        try {
            const l = await getLogin()
            queueRefreshLogin(l)
            console.log("login refreshed")
        } catch (e) {
            console.log("refreshing login failed", e)
        }
    }

    const queueRefreshLogin = (l: Login) => {
        if (l) {
            const diff = getDiff(l)
            console.log("refresh login in", diff)
            if (refreshTimeout) clearTimeout(refreshTimeout)
            refreshTimeout = setTimeout(refreshLogin, diff)
        } else {
            if (refreshTimeout) clearTimeout(refreshTimeout)
        }
    }

    login.subscribe((l) => {
        queueRefreshLogin(l)
    })
}

////////////////////// CART

let cartInit: CartItem[] = []
const cartStr = typeof window !== "undefined" && window.localStorage?.getItem("cart")
if (cartStr) {
    cartInit = JSON.parse(cartStr)
}
export const cart = writable(cartInit)

export function addItemToCart(i: CartItem) {
    // build random _uuid
    i._uuid = Math.random().toString(36).substring(2)
    // append to cart
    cart.update((c) => {
        c.push(i)
        return c
    })
}

export function setCartItemQuantity(i: CartItem, quantity: number) {
    if (quantity < 1) {
        // remove item by _uuid
        cart.update((c) => c.filter((_i) => _i._uuid != i._uuid))
        return
    }
    // use _uuid to identify item because of individial configuration
    cart.update((c) => {
        const _i = c.find((_i) => _i._uuid == i._uuid)
        if (_i) {
            _i.quantity = quantity
        }
        return c
    })
}

let checkoutDataInit: {
    stepsValid: 0 | 1 | 2 | 3
    localStorage: boolean
    contact: Contact
    deliveryAddress: Address
    billingAddressInit: boolean
    billingAddress: Address
    commission: string
    sameAddress: boolean
    vat: number
    payment: Payment
} = {
    stepsValid: 0, // enable/disable checkout steps
    localStorage: false,

    contact: {
        email: "",
        phone: "",
    },

    deliveryAddress: {
        firstname: "",
        lastname: "",
        company: "",
        street: "",
        houseNumber: "",
        postcode: "",
        city: "",
        countryCode: "DE",
    },

    billingAddressInit: true, // flagging not initialized billingAddress, see payment component
    billingAddress: {
        firstname: "",
        lastname: "",
        company: "",
        street: "",
        houseNumber: "",
        postcode: "",
        city: "",
        countryCode: "DE",
    },

    commission: "",

    sameAddress: true,

    vat: defaultVAT,

    payment: {
        type: "paypal",
    },
}

const checkoutDataStr = typeof window !== "undefined" && window.sessionStorage?.getItem("checkoutData")
if (checkoutDataStr) {
    checkoutDataInit = JSON.parse(checkoutDataStr)
} else {
    const checkoutDataStr = typeof window !== "undefined" && window.localStorage?.getItem("checkoutData")
    if (checkoutDataStr) {
        checkoutDataInit = JSON.parse(checkoutDataStr)
    }
}

export const checkoutData = writable(checkoutDataInit)

export async function getAsyncCartSum(_cart: CartItem[], deliveryCountry?: Country): Promise<CartSum> {
    const l = get(login)
    const c = get(checkoutData)

    let vat = 0
    if (!l) {
        vat = c.vat
    }

    const sum = SSR.getCartSum(_cart, vat, l && _cart?.[0]?.product?.price_net_dealer_is_fixed) // Verpackungskosten nur für Händler und Festpreisprodukte

    const country = deliveryCountry || (await getCountry(l ? "DE" : c?.deliveryAddress?.countryCode || "DE"))
    if (l) {
        if (_cart?.[0]?.product?.price_net_dealer_is_fixed) {
            // Festpreisprodukte
            SSR.setShippingInSum(sum, SSR.getShippingCostsByFactor(_cart), vat)
        }
    } else {
        if (country) {
            SSR.setShippingInSum(sum, (country.delivery_costs_net || 0) + (country.handling_costs_net || 0), vat)
            SSR.setMinQtySurchargeInSum(sum, l ? 0 : country.min_quantity_surcharge_net || 0, vat) // Mindermengenzuschlag nicht für Händler
        }
    }

    SSR.setTotalSum(sum)

    return sum
}

cart.subscribe((c) => {
    typeof window !== "undefined" && window.localStorage?.setItem("cart", JSON.stringify(c))
})

checkoutData.subscribe((d) => {
    if (typeof window !== "undefined") {
        window.sessionStorage?.setItem("checkoutData", JSON.stringify(d))
        if (d.localStorage) {
            window.localStorage?.setItem("checkoutData", JSON.stringify(d))
        } else {
            window.localStorage?.removeItem("checkoutData")
        }
    }
})

async function rereadCartProducts(l: Login) {
    const c = get(cart)
    const { data: p } = await getProducts(
        {
            _id: { $in: c.map((i) => i.product.id) },
        },
        null,
        null,
        null,
        "full"
    )
    const pMap: { [key: string]: Product } = {}
    p.forEach((i) => {
        pMap[i.id] = i
    })

    c.forEach((i) => {
        i.product = pMap[i.product.id]
        // price can also change on login/logout
        i.price.base = l ? i.product.price_net_dealer : i.product.price_net_end_customer
        i.price.unit = getProductPrice(i.product, null, null, !!l)

        // add missing _uuid (fallback for older versions)
        if (!i._uuid) {
            i._uuid = Math.random().toString(36).substring(2)
        }
    })

    // remove items with no product
    c.filter((i) => i.product)

    cart.set(c)
}

login.subscribe((l) => {
    // recalculate cart on every dealer login/logout and on load
    if (typeof l !== "undefined") {
        rereadCartProducts(l)

        if (!l) {
            // empty commission on logout
            checkoutData.update((ckd) => {
                ckd.commission = ""
                return ckd
            })
        }
    }
})

//////////////////////////// Meine Bestellungen

export const myOrdersToken = writable("")

/////////////////////////// Paypal

// export const pppApproval = writable({ url: "", total: 0 })

/////////////////////////// eConfig

export const openRalChooserOverlay = writable<Function>(undefined)
export const openOptionsChooserOverlay = writable<Function>(undefined)

export const preselectedDealerNumber = writable<string>(null)
if (typeof window !== "undefined") {
    let _preselectedDealerNumber = getParameterByName("d")
    if (!_preselectedDealerNumber) {
        _preselectedDealerNumber = window.sessionStorage.getItem("preselectedDealerNumber")
    }

    preselectedDealerNumber.subscribe(function (n) {
        if (n) {
            window.sessionStorage.setItem("preselectedDealerNumber", n)
        } else {
            window.sessionStorage.removeItem("preselectedDealerNumber")
        }
    })
    if (_preselectedDealerNumber) {
        preselectedDealerNumber.set(_preselectedDealerNumber)
    }
}

export const iframeView = writable<boolean>(false)
if (typeof window !== "undefined") {
    let _iframeView = getParameterByName("iframe")
    if (!_iframeView) {
        _iframeView = window.sessionStorage.getItem("iframeView")
    }
    iframeView.subscribe(function (n) {
        if (n) {
            window.sessionStorage.setItem("iframeView", "1")
        } else {
            window.sessionStorage.removeItem("iframeView")
        }
    })
    if (_iframeView) {
        iframeView.set(true)
    }
}

export const referrer = writable<string>(null)
if (typeof document !== "undefined" && typeof window) {
    let _referrer = window.sessionStorage.getItem("referrer")

    // if stored referrer other host than current host
    if (_referrer && _referrer.indexOf(document.location.host) < 0) {
        // use it
    } else {
        _referrer = document.referrer
        window.sessionStorage.setItem("referrer", _referrer)
    }

    console.log("Referrer: ", document.referrer, _referrer)

    referrer.set(_referrer)
}

export const theme = writable<string>("light")
if (typeof window !== "undefined") {
    let _theme = getParameterByName("theme")
    if (!_theme) {
        _theme = window.sessionStorage.getItem("theme")
    }
    theme.subscribe(function (n) {
        if (n) {
            window.sessionStorage.setItem("theme", n)
        } else {
            window.sessionStorage.removeItem("theme")
        }
    })
    if (_theme) {
        theme.set(_theme)
    }
}

export const customizedTheme = writable<string>(null)
if (typeof window !== "undefined") {
    let _customizedTheme = getParameterByName("customized-theme")
    if (!_customizedTheme && typeof _customizedTheme !== "string") {
        _customizedTheme = window.sessionStorage.getItem("theme")
    }
    customizedTheme.subscribe(function (n) {
        if (n) {
            window.sessionStorage.setItem("customized-theme", JSON.stringify(n))

            // n is given p.e. as "background|ffffff,body|ffffff"
            // so we need to split it and set the css variables
            let vList = n.split(",").map((item) => {
                let i = item.split("|")
                return `--${i[0]}: #${i[1]}`
            })
            document.body.style.cssText = vList.join(";")
        } else {
            window.sessionStorage.removeItem("customized-theme")
            document.body.removeAttribute("style")
        }
    })
    if (typeof _customizedTheme === "string") {
        customizedTheme.set(_customizedTheme)
    }
}

export interface PredefinedContact {
    customerType: string
    firstname: string
    lastname: string
    company: string
    city: string
    street: string
    houseNumber: string
    postcode: string
    countryCode: string
    email: string
    phone: string
}
export const predefinedContact = writable<PredefinedContact>(null)
if (typeof window !== "undefined") {
    const predef: PredefinedContact = {
        customerType: getParameterByName("customerType"),
        firstname: getParameterByName("firstname"),
        lastname: getParameterByName("lastname"),
        company: getParameterByName("company"),
        city: getParameterByName("city"),
        street: getParameterByName("street"),
        houseNumber: getParameterByName("houseNumber"),
        postcode: getParameterByName("postcode"),
        countryCode: getParameterByName("countryCode"),
        email: getParameterByName("email"),
        phone: getParameterByName("phone"),
    }

    if (predef.email) {
        predefinedContact.set(predef)
    }
}

/////////////////////////// Offer Assistant

export let showOfferAssistant = writable<boolean>(false)

// notifications
interface NotificationEntry {
    html: string
    class: "info" | "success" | "warning" | "error"
    timeout?: number
    removeOnNewLocation?: boolean
    blockUI?: boolean
}
export const notifications = writable<NotificationEntry[]>([])
export function newNotification(n: NotificationEntry): NotificationEntry {
    notifications.update((_n) => {
        _n.push(n)
        let timeout = n.timeout
        if (!timeout) timeout = 3500 // default
        if (timeout > 0) {
            setTimeout(() => {
                removeNotification(n)
            }, timeout)
        }
        return _n
    })

    return n
}
export function removeNotification(n: NotificationEntry) {
    notifications.update((_n) => {
        return _n.filter((v) => {
            return v !== n
        })
    })
}

location.subscribe((l) => {
    notifications.update((_n) => {
        return _n.filter((v) => {
            return !v.removeOnNewLocation
        })
    })
})

export const openModal = writable<boolean>(false)
